"use client";
import React from "react";
import { motion } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowUpRight01Icon,
  FlashIcon,
  Layers01Icon,
  CursorPointer01Icon,
  SparklesIcon,
  UserGroupIcon,
  File01Icon,
} from "@hugeicons/core-free-icons";
import { ProjectType } from "@/components/utils/types";

interface CoreFeaturesProps {
  project: ProjectType;
}

export const CoreFeatures = ({ project }: CoreFeaturesProps) => {
  return (
    <section className="relative w-full py-24 md:py-32 px-6 md:px-20 z-20 overflow-hidden">
      {/* Background Gradient Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-sky-500/10 dark:bg-sky-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 100, damping: 20 }}
          className="flex flex-col items-center justify-center text-center mb-8"
        >
          <h2 className="font-recoleta text-4xl md:text-6xl lg:text-7xl text-black dark:text-white mb-2">
            Core{" "}
            <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
              Features
            </span>
          </h2>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
          {project.features?.map((feature, idx) => {
            const icons = [
              FlashIcon,
              Layers01Icon,
              CursorPointer01Icon,
              SparklesIcon,
              UserGroupIcon,
              File01Icon,
            ];
            const Icon = icons[idx] || FlashIcon;

            // Gradient colors for each card
            const gradients = [
              "from-sky-500/30 to-blue-600/30 dark:from-sky-500/20 dark:to-blue-600/20",
              "from-purple-500/30 to-pink-500/30 dark:from-purple-500/20 dark:to-pink-500/20",
              "from-green-500/30 to-emerald-500/30 dark:from-green-500/20 dark:to-emerald-500/20",
              "from-orange-500/30 to-amber-500/30 dark:from-orange-500/20 dark:to-amber-500/20",
              "from-rose-500/30 to-red-500/30 dark:from-rose-500/20 dark:to-red-500/20",
              "from-indigo-500/30 to-violet-500/30 dark:from-indigo-500/20 dark:to-violet-500/20",
            ];

            const iconColors = [
              "text-sky-500 dark:text-sky-400",
              "text-purple-500 dark:text-purple-400",
              "text-green-500 dark:text-green-400",
              "text-orange-500 dark:text-orange-400",
              "text-rose-500 dark:text-rose-400",
              "text-indigo-500 dark:text-indigo-400",
            ];

            const borderColors = [
              "border-sky-500/20 dark:border-sky-400/20 hover:border-sky-500/40 dark:hover:border-sky-400/40",
              "border-purple-500/20 dark:border-purple-400/20 hover:border-purple-500/40 dark:hover:border-purple-400/40",
              "border-green-500/20 dark:border-green-400/20 hover:border-green-500/40 dark:hover:border-green-400/40",
              "border-orange-500/20 dark:border-orange-400/20 hover:border-orange-500/40 dark:hover:border-orange-400/40",
              "border-rose-500/20 dark:border-rose-400/20 hover:border-rose-500/40 dark:hover:border-rose-400/40",
              "border-indigo-500/20 dark:border-indigo-400/20 hover:border-indigo-500/40 dark:hover:border-indigo-400/40",
            ];

            // Determine card size - first and last cards are larger
            const isLarge = idx === 0 || idx === 5;

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 100,
                  damping: 15,
                  delay: idx * 0.1,
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  transition: { type: "spring", stiffness: 300, damping: 20 },
                }}
                className={`group relative p-6 md:p-8 rounded-3xl border backdrop-blur-xl bg-white/50 dark:bg-white/5 transition-all duration-500
                  ${borderColors[idx]}
                  ${isLarge ? "lg:col-span-2" : ""}`}
              >
                {/* Card Background Gradient */}
                <div
                  className={`absolute inset-0 rounded-3xl bg-linear-to-br ${gradients[idx]} opacity-0 group-hover:opacity-30 transition-opacity duration-500`}
                />

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon Container */}
                  <motion.div
                    initial={{ rotate: 0 }}
                    whileHover={{ rotate: 12, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className={`inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-linear-to-br ${gradients[idx]} mb-6`}
                  >
                    <HugeiconsIcon
                      icon={Icon}
                      className={`w-7 h-7 md:w-8 md:h-8 ${iconColors[idx]}`}
                    />
                  </motion.div>

                  {/* Title */}
                  <h3 className="font-recoleta text-xl md:text-2xl text-black dark:text-white mb-3 group-hover:text-sky-600 dark:group-hover:text-sky-400 transition-colors duration-300">
                    {feature}
                  </h3>

                  {/* Description */}
                  <p className="font-outfit text-base text-black/60 dark:text-white/60 leading-relaxed">
                    {project.featureDescription?.[idx] || ""}
                  </p>

                  {/* Hover Arrow */}
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    whileHover={{ opacity: 1, x: 0 }}
                    className="absolute bottom-6 right-6 md:bottom-8 md:right-8"
                  >
                    <HugeiconsIcon
                      icon={ArrowUpRight01Icon}
                      className={`w-6 h-6 ${iconColors[idx]} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
                    />
                  </motion.div>
                </div>

                {/* Decorative Corner Gradient */}
                <div
                  className={`absolute -bottom-20 -right-20 w-40 h-40 bg-linear-to-br ${gradients[idx]} rounded-full blur-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
